import {CommonModule} from '@angular/common';
import {ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {ToastService} from '../../shared/commons/toast/toast-service';
import {UserProfileService} from '../user-profile/user-profile.service';
import {AddCertificateModalComponent, CertificateEditValue, CertificateModalResult} from './user-certificate-modal';
import {UserCertificateApiService} from './user-certificate-api.service';
import {PageTitleComponent} from '../../shared/commons/page-title/page-title.component';
import {CertificateCreateRequest, CertificateItemDto as Certificate} from '../../../models/generated-models';
import {ConfirmDialogService} from '../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';

interface CurrentOwner {
  name: string;
  taxId: string;
}

@Component({
  selector: 'app-user-certificate',
  imports: [
    CommonModule,
    FormsModule,
    PageTitleComponent
  ],
  templateUrl: './user-certificate.html',
  styleUrl: './user-certificate.scss',
})
export class UserCertificate implements OnInit {
  private readonly modalService = inject(NgbModal);
  private readonly profileService = inject(UserProfileService);
  private readonly route = inject(ActivatedRoute);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly certificateApi = inject(UserCertificateApiService);
  private readonly toast = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly confirmDialog = inject(ConfirmDialogService);

  isLoading = false;
  isSyncing = false;
  searchTerm = '';
  currentOwner: CurrentOwner = {name: 'ยังไม่ระบุ', taxId: '-'};
  allCertificates: Certificate[] = [];
  certificates: Certificate[] = [];
  private initialSearchTerm = '';

  ngOnInit(): void {
    this.initialSearchTerm = this.route.snapshot.queryParamMap.get('search')?.trim() ?? '';
    this.searchTerm = this.initialSearchTerm;
    this.loadCurrentOwner();
  }

  get activeCertificateCount(): number {
    return this.allCertificates.filter((certificate) => certificate.status === 'active').length;
  }

  get expiredCertificateCount(): number {
    return this.allCertificates.filter((certificate) => certificate.status === 'expired').length;
  }

  getStatusText(status: 'active' | 'expired'): string {
    return status === 'active' ? 'ใช้งาน' : 'หมดอายุ';
  }

  getStatusClass(status: 'active' | 'expired'): string {
    return status === 'active' ? 'status-active' : 'status-expired';
  }

  searchCertificate(): void {
    this.applySearch();
  }

  refreshData(): void {
    this.loadCertificates();
  }

  syncCertificates(): void {
    if (this.isSyncing) {
      return;
    }
    this.confirmDialog.open({
      title: 'ซิงค์ใบรับรอง',
      content: 'ต้องการซิงค์ใบรับรองจาก ThaiTraces ใช่หรือไม่',
      confirmButtonMsg: 'ซิงค์',
      closeButtonMsg: 'ยกเลิก'
    }).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      setTimeout(() => this.performSync());
    });
  }

  private performSync(): void {
    this.isSyncing = true;
    this.cdr.detectChanges();
    this.certificateApi.syncCertificates().subscribe({
      next: (items) => {
        this.allCertificates = items;
        this.applySearch();
        this.isSyncing = false;
        this.cdr.detectChanges();
        this.toast.success('ซิงค์ใบรับรองเรียบร้อยแล้ว');
      },
      error: () => {
        this.isSyncing = false;
        this.cdr.detectChanges();
      }
    });
  }

  addCertificate(): void {
    const modalRef = this.modalService.open(AddCertificateModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      windowClass: 'user-certificate-modal'
    });
    modalRef.componentInstance.ownerName = this.currentOwner.name;
    modalRef.componentInstance.taxId = this.currentOwner.taxId;
    modalRef.componentInstance.mode = 'create';

    modalRef.result.then((result: CertificateModalResult | undefined) => {
      if (!result) {
        return;
      }
      const {attachment, removeAttachment, ...request} = result;
      this.certificateApi.createCertificate(request as CertificateCreateRequest).subscribe({
        next: (saved) => {
          this.allCertificates = [saved, ...this.allCertificates];
          this.searchTerm = '';
          this.applySearch();
          this.cdr.detectChanges();
          this.toast.success('บันทึกใบรับรองเรียบร้อยแล้ว');
          if (attachment && saved.id != null) {
            this.attachAfterSave(saved.id, attachment);
          }
        }
        // errors surfaced by the global HTTP interceptors
      });
    }).catch(() => undefined);
  }

  viewCertificate(certificate: Certificate): void {
    const modalRef = this.modalService.open(AddCertificateModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: true,
      windowClass: 'user-certificate-modal'
    });
    modalRef.componentInstance.ownerName = certificate.ownerName;
    modalRef.componentInstance.taxId = certificate.taxId;
    modalRef.componentInstance.mode = 'view';
    modalRef.componentInstance.initialValue = {
      id: certificate.id,
      certificateNo: certificate.certificateNo,
      standardCode: certificate.standardCode,
      standardName: certificate.standardName,
      productName: certificate.productName,
      issuingDepartment: certificate.issuingDepartment,
      issuingDepartmentName: certificate.issuingDepartmentName,
      attachmentUrl: certificate.attachmentUrl,
      attachmentName: certificate.attachmentName,
      description: certificate.description,
      sizeFarm: certificate.sizeFarm,
      unitFarm: certificate.unitFarm,
      startDate: certificate.startDate,
      endDate: certificate.endDate
    } satisfies CertificateEditValue;
  }

  editCertificate(certificate: Certificate): void {
    const modalRef = this.modalService.open(AddCertificateModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      windowClass: 'user-certificate-modal'
    });
    modalRef.componentInstance.ownerName = certificate.ownerName;
    modalRef.componentInstance.taxId = certificate.taxId;
    modalRef.componentInstance.mode = 'edit';
    modalRef.componentInstance.initialValue = {
      id: certificate.id,
      certificateNo: certificate.certificateNo,
      standardCode: certificate.standardCode,
      standardName: certificate.standardName,
      productName: certificate.productName,
      issuingDepartment: certificate.issuingDepartment,
      issuingDepartmentName: certificate.issuingDepartmentName,
      attachmentUrl: certificate.attachmentUrl,
      attachmentName: certificate.attachmentName,
      description: certificate.description,
      sizeFarm: certificate.sizeFarm,
      unitFarm: certificate.unitFarm,
      startDate: certificate.startDate,
      endDate: certificate.endDate
    } satisfies CertificateEditValue;

    modalRef.result.then((result: CertificateModalResult | undefined) => {
      if (!result) {
        return;
      }
      const {attachment, removeAttachment, ...request} = result;
      this.certificateApi.updateCertificate(certificate.id, request as CertificateCreateRequest).subscribe({
        next: (updated) => {
          this.replaceCertificate(updated);
          this.toast.success('แก้ไขใบรับรองเรียบร้อยแล้ว');
          if (attachment) {
            this.attachAfterSave(certificate.id, attachment);
          } else if (removeAttachment) {
            this.certificateApi.removeAttachment(certificate.id).subscribe({
              next: (withoutFile) => this.replaceCertificate(withoutFile)
            });
          }
        }
        // errors surfaced by the global HTTP interceptors
      });
    }).catch(() => undefined);
  }

  /** The scan is a second request after the save; the certificate stays even if it fails. */
  private attachAfterSave(id: number, file: File): void {
    this.certificateApi.uploadAttachment(id, file).subscribe({
      next: (withFile) => this.replaceCertificate(withFile)
      // errors surfaced by the global HTTP interceptors
    });
  }

  private replaceCertificate(updated: Certificate): void {
    this.allCertificates = this.allCertificates.map((item) => item.id === updated.id ? updated : item);
    this.applySearch();
    this.cdr.detectChanges();
  }

  deleteCertificate(certificate: Certificate): void {
    const confirmed = window.confirm(`ต้องการลบใบรับรอง ${certificate.certificateNo} ใช่หรือไม่`);
    if (!confirmed) {
      return;
    }
    this.certificateApi.deleteCertificate(certificate.id).subscribe({
      next: () => {
        this.allCertificates = this.allCertificates.filter((item) => item.id !== certificate.id);
        this.applySearch();
        this.cdr.detectChanges();
        this.toast.success('ลบใบรับรองเรียบร้อยแล้ว');
      }
      // errors surfaced by the global HTTP interceptors
    });
  }

  private loadCurrentOwner(): void {
    this.isLoading = true;
    this.profileService.getPersonalProfile().subscribe({
      next: (profile) => {
        this.currentOwner = {
          name: profile.fullName?.trim() || profile.username || 'ยังไม่ระบุ',
          taxId: profile.cardNo?.trim() || '-'
        };
        this.cdr.detectChanges();
        this.loadCertificates();
      },
      error: () => {
        this.oidcSecurityService.getUserData().subscribe({
          next: (userData: any) => {
            const name = `${userData?.name || userData?.fullName || userData?.username || 'ยังไม่ระบุ'}`.trim();
            const taxId = `${userData?.cardNo || userData?.taxId || '-'}`.trim();
            this.currentOwner = {
              name: name || 'ยังไม่ระบุ',
              taxId: taxId || '-'
            };
            this.cdr.detectChanges();
            this.loadCertificates();
          },
          error: () => {
            this.loadCertificates();
          }
        });
      }
    });
  }

  private loadCertificates(): void {
    this.isLoading = true;
    this.certificateApi.listCertificates().subscribe({
      next: (items) => {
        this.allCertificates = items;
        this.searchTerm = this.initialSearchTerm;
        this.isLoading = false;
        this.applySearch();
        this.cdr.detectChanges();
      },
      error: () => {
        this.allCertificates = [];
        this.searchTerm = this.initialSearchTerm;
        this.isLoading = false;
        this.applySearch();
        this.cdr.detectChanges();
      }
    });
  }

  private applySearch(): void {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.certificates = [...this.allCertificates];
      return;
    }
    this.certificates = this.allCertificates.filter((certificate) =>
      [
        certificate.certificateNo,
        certificate.ownerName,
        certificate.taxId,
        certificate.standardCode,
        certificate.standardName,
        certificate.productName,
        certificate.issuingDepartment?.toString(),
        certificate.issuingDepartmentName,
        certificate.description,
        certificate.sizeFarm?.toString(),
        certificate.unitFarm
      ]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(term))
    );
  }

  protected formatDate(value: Date | string | undefined): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('th-TH');
  }

  protected ownerLabel(certificate: Certificate): string {
    return `${certificate.ownerName ?? '-'} - ${certificate.taxId ?? '-'}`;
  }

}
